import React, { useEffect } from "react"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { ShimmerThumbnail } from "react-shimmer-effects"
import { Badge, Card, CardBody, CardTitle, Col, Row, Table } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import { getItemsDetails } from "store/actions/itemsAction"
import formatDateTime from "components/Shared/formatDateTime"
import { useTranslation } from "react-i18next"

const ComboDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Combo Details ")
  const dispatch = useDispatch()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")
  const { loading, itemDetails } = useSelector(state => state.Items)

  useEffect(() => {
    getOldValues()
  }, [])

  const getOldValues = () => {
    const payload = {
      id,
    }
    dispatch(getItemsDetails(payload))
  }
  return (
    <div className="page-content ">
      <div className="container-fluid">
        <Breadcrumbs />
        {loading ? (
          <Card>
            <CardBody>
              <Row>
                <Col lg={8}>
                  <ShimmerThumbnail height={700} rounded />
                </Col>
                <Col lg={4}>
                  <ShimmerThumbnail height={700} rounded />
                </Col>
              </Row>
            </CardBody>
          </Card>
        ) : (
          <>
            <Row>
              <Col lg={8}>
                <Card>
                  <CardBody>
                    <CardTitle className="mb-3 d-flex align-items-center">
                      <BackButton /> {t("Combo Details")}
                    </CardTitle>
                    <div className="table-responsive">
                      {Object.values(itemDetails).length === 0 ? (
                        <p className="text-center">
                          {t("No combo details found")}
                        </p>
                      ) : (
                        <Table className="table-nowrap mb-0">
                          <tbody>
                            <tr>
                              <th scope="row">{t("Id")}</th>
                              <td>{itemDetails._id}</td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Name(English)")}</th>
                              <td>{itemDetails.name || "-"}</td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Name(عربي)")}</th>
                              <td>{itemDetails.nameAr || "-"}</td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Description(English)")}</th>
                              <td>{itemDetails.description || "-"}</td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Description(عربي)")}</th>
                              <td>{itemDetails.descriptionAr || "-"}</td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Display Order")}</th>
                              <td>{itemDetails.displayOrder ?? "-"}</td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Is Discount Allowed")}</th>
                              <td>
                                {itemDetails.isDiscountAllowed
                                  ? t("Yes")
                                  : t("No")}
                              </td>
                            </tr>
                            {itemDetails.isDiscountAllowed && (
                              <tr>
                                <th scope="row">{t("Discount Percentage")}</th>
                                <td>{itemDetails.discount} %</td>
                              </tr>
                            )}
                            <tr>
                              <th scope="row">{t("Price")}</th>
                              <td>
                                {itemDetails.price}{" "}
                                {itemDetails.countryCode === "SA"
                                  ? "SAR"
                                  : "RS"}
                              </td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Original Price")}</th>
                              <td>
                                {itemDetails.originalPrice ?? "-"}{" "}
                                {itemDetails.countryCode === "SA"
                                  ? "SAR"
                                  : "RS"}
                              </td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Calories")}</th>
                              <td>{itemDetails.calories ?? "-"}</td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Calorie Count")}</th>
                              <td>{itemDetails.calorieCount ?? "-"}</td>
                            </tr>
                            <tr>
                              <th scope="row">
                                {t("Person Count Per Serving")}
                              </th>
                              <td>
                                {itemDetails.personCountPerServing ?? "-"}
                              </td>
                            </tr>

                            <tr>
                              <th scope="row">{t("Category Name")}</th>
                              <td>
                                {language === "sa"
                                  ? itemDetails?.categoryId?.nameAr
                                  : itemDetails?.categoryId?.name || "-"}
                              </td>
                            </tr>
                            {itemDetails?.conditions
                              ?.hasSpecificDateAndTime && (
                              <>
                                <tr>
                                  <th scope="row">{t("Start Date & Time")}</th>
                                  <td>
                                    {formatDateTime(
                                      itemDetails?.conditions?.startDateAndTime
                                    ) || "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">{t("End Date & Time")}</th>
                                  <td>
                                    {formatDateTime(
                                      itemDetails?.conditions?.endDateAndTime
                                    ) || "-"}
                                  </td>
                                </tr>
                              </>
                            )}

                            <tr>
                              <th scope="row">
                                {t("Disable For Specific Branches")}
                              </th>
                              <td>
                                <div className="text-wrap">
                                  {itemDetails?.disabledBranches.length === 0
                                    ? "-"
                                    : itemDetails?.disabledBranches?.map(
                                        item =>
                                          `${
                                            language === "sa"
                                              ? item.nameAr
                                              : item.name
                                          }, `
                                      )}
                                </div>
                              </td>
                            </tr>

                            <tr>
                              <th scope="row">{t("Status")}</th>
                              <td>
                                {itemDetails.status === 1 ? (
                                  <Badge className="bg-success">
                                    {t("Active")}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-danger">
                                    {t("Deactive")}
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col lg={4}>
                <Card className="card_100_24">
                  <CardBody>
                    <img
                      src={itemDetails.imageUrl}
                      alt=""
                      className="img-fluid d-block mx-auto"
                      style={{ height: "100%", objectFit: "contain" }}
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Row>
              <Col>
                <Card className="card_100_24">
                  <CardBody>
                    <CardTitle>{t("Combo Items")}</CardTitle>
                    {itemDetails?.items?.length === 0 ? (
                      <p className="text-center my-3">
                        {t("No combo items found")}
                      </p>
                    ) : (
                      <div className="table-responsive">
                        <Table className="table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>{t("Image")}</th>
                              <th>{t("Name")}</th>
                              <th>{t("Quantity")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {itemDetails?.items?.map((item, index) => (
                              <tr key={item._id}>
                                <td scope="row">
                                  {
                                    <img
                                      src={item?.itemId?.imageUrl}
                                      alt=""
                                      style={{
                                        maxWidth: 50,
                                        maxHeight: 50,
                                        objectFit: "contain",
                                      }}
                                    />
                                  }
                                </td>
                                <td scope="row">
                                  <p className="fw-bold mb-2">
                                    {language === "sa"
                                      ? item?.itemId?.nameAr
                                      : item?.itemId?.name}
                                  </p>
                                  {item?.modifierGroups?.map(subItem => (
                                    <React.Fragment key={subItem._id}>
                                      <span>
                                        {language === "sa"
                                          ? subItem?.nameAr
                                          : subItem?.name}
                                      </span>
                                      <br />
                                    </React.Fragment>
                                  ))}
                                </td>

                                <td scope="row">{item?.quantity}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      // ))
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </div>
    </div>
  )
}

export default ComboDetails
